import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { ApplicationConfigService } from 'src/app/core/config/application-config.service';
import { CommonService } from 'src/app/shared/services/common.service';
import {
  RiskGradingScoreService,
  RiskGradingService,
  TransactionProfileLimitService,
  TransactionProfileService,
} from 'src/app/admin/assisted-ekyc/configuration/configuration-api.service';
import {
  EkycTier,
  IRiskGrading,
  IRiskGradingScore,
  ITransactionProfile,
  ITransactionProfileLimit,
} from 'src/app/admin/assisted-ekyc/configuration/configuration.model';

import { MockDb } from '../mock-db';
import {
  IRiskBand,
  RISK_BAND_SEED,
  RISK_GRADING_SCORE_SEED,
  RISK_GRADING_SEED,
  RISK_MODEL_VERSION,
  TRANSACTION_PROFILE_LIMIT_SEED,
  TRANSACTION_PROFILE_SEED,
} from '../fixtures/configuration.fixture';

const MOCK_LATENCY_MS = 250;

export const CONFIG_COLLECTION = {
  riskGrading: 'riskGrading',
  riskGradingScore: 'riskGradingScore',
  transactionProfile: 'transactionProfile',
  transactionProfileLimit: 'transactionProfileLimit',
} as const;

// ---------------------------------------------------------------------------------------------
// Store access — seeded lazily so a fixture edit shows up without a schema bump for collections
// that have never been written to.
// ---------------------------------------------------------------------------------------------

function read<T>(collection: string, seedValue: T[]): T[] {
  const stored = MockDb.get<T[]>(collection);
  if (!stored) {
    MockDb.set(collection, seedValue);
    return [...seedValue];
  }
  return stored;
}

function write<T>(collection: string, rows: T[]): void {
  MockDb.set(collection, rows);
}

export const ConfigStore = {
  riskGradings: (): IRiskGrading[] => read(CONFIG_COLLECTION.riskGrading, RISK_GRADING_SEED),
  riskGradingScores: (): IRiskGradingScore[] => read(CONFIG_COLLECTION.riskGradingScore, RISK_GRADING_SCORE_SEED),
  transactionProfiles: (): ITransactionProfile[] => read(CONFIG_COLLECTION.transactionProfile, TRANSACTION_PROFILE_SEED),
  transactionProfileLimits: (): ITransactionProfileLimit[] =>
    read(CONFIG_COLLECTION.transactionProfileLimit, TRANSACTION_PROFILE_LIMIT_SEED),

  riskBands: (): IRiskBand[] => RISK_BAND_SEED,
  riskModelVersion: (): string => RISK_MODEL_VERSION,

  /** Active grading parameters with their selectable scores attached, ordered by position. */
  activeGradingModel(): IRiskGrading[] {
    const scores = ConfigStore.riskGradingScores();
    return ConfigStore.riskGradings()
      .filter((grading) => grading.status)
      .sort((a, b) => a.position - b.position)
      .map((grading) => ({
        ...grading,
        riskGradingScores: scores.filter((score) => score.riskGradingCode === grading.code),
      }));
  },

  /**
   * Active transaction profiles for a tier. This is what makes the Step 1 tier pick binding:
   * Step 5 offers only these, so an over-limit slab is never selectable (Amendment 1, Q1).
   */
  profilesForTier(tier: EkycTier): ITransactionProfile[] {
    return ConfigStore.transactionProfiles().filter(
      (profile) => profile.status && profile.transactionProfileSlab?.ekycType === tier,
    );
  },

  limitsForProfile(transactionProfileCode: string): ITransactionProfileLimit[] {
    return ConfigStore.transactionProfileLimits().filter(
      (limit) => limit.status && limit.transactionProfileCode === transactionProfileCode,
    );
  },

  bandForScore(totalScore: number): IRiskBand {
    const bands = ConfigStore.riskBands();
    return (
      bands.find((band) => totalScore >= band.minScore && totalScore <= band.maxScore) ?? bands[bands.length - 1]
    );
  },
};

// ---------------------------------------------------------------------------------------------
// Generic list / find / save behaviour shared by the four mock services
// ---------------------------------------------------------------------------------------------

export interface PagedEnvelope<T> {
  responseCode: string;
  responseMessage: string;
  data: { data: T[]; currentPage: number; pageSize: number; totalItems: number };
}

function matches(value: unknown, filter: unknown): boolean {
  if (filter === null || filter === undefined || filter === '') return true;
  return String(value ?? '')
    .toLowerCase()
    .includes(String(filter).toLowerCase());
}

function equals(value: unknown, filter: unknown): boolean {
  if (filter === null || filter === undefined || filter === '') return true;
  return String(value ?? '') === String(filter);
}

function page<T>(rows: T[], queryModel: any): PagedEnvelope<T> {
  const pageSize = Number(queryModel?.pageSize) || 10;
  const pageNumber = Number(queryModel?.pageNumber) || 0;
  const start = pageNumber * pageSize;
  return {
    responseCode: 'S100000',
    responseMessage: 'Operation successful',
    data: {
      data: rows.slice(start, start + pageSize),
      currentPage: pageNumber,
      pageSize,
      totalItems: rows.length,
    },
  };
}

function envelope<T>(data: T) {
  return { responseCode: 'S100000', responseMessage: 'Operation successful', data };
}

function respond<T>(value: T): Observable<T> {
  return of(value).pipe(delay(MOCK_LATENCY_MS));
}

/** Next sequential code for a collection, e.g. RG-009. */
function nextCode(prefix: string, existing: string[]): string {
  const highest = existing.reduce((max, code) => {
    const n = Number(code.split('-')[1]);
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return `${prefix}-${String(highest + 1).padStart(3, '0')}`;
}

// ---------------------------------------------------------------------------------------------
// Risk Grading
// ---------------------------------------------------------------------------------------------

@Injectable()
export class MockRiskGradingService extends RiskGradingService {
  constructor(http: HttpClient, commonService: CommonService, appConfig: ApplicationConfigService) {
    super(http, commonService, appConfig);
  }

  override query(queryModel?: any): Observable<any> {
    const rows = ConfigStore.riskGradings()
      .filter((r) => matches(r.description, queryModel?.description))
      .filter((r) => equals(r.riskGradingScoreType, queryModel?.scoreType))
      .filter((r) => equals(r.status, queryModel?.status))
      .sort((a, b) => a.position - b.position);
    return respond(page(rows, queryModel));
  }

  override find(code: string): Observable<any> {
    return respond(envelope(ConfigStore.riskGradings().find((r) => r.code === code) ?? null));
  }

  override create(data: any): Observable<any> {
    const rows = ConfigStore.riskGradings();
    const created: IRiskGrading = {
      ...data,
      code: nextCode('RG', rows.map((r) => r.code)),
      status: data.status ?? true,
      pendingApproval: true,
    };
    write(CONFIG_COLLECTION.riskGrading, [...rows, created]);
    return respond(envelope(created));
  }

  override update(data: any): Observable<any> {
    const rows = ConfigStore.riskGradings().map((row) =>
      row.code === data.code ? { ...row, ...data, pendingApproval: true } : row,
    );
    write(CONFIG_COLLECTION.riskGrading, rows);
    return respond(envelope(rows.find((r) => r.code === data.code) ?? null));
  }
}

// ---------------------------------------------------------------------------------------------
// Risk Grading Score
// ---------------------------------------------------------------------------------------------

@Injectable()
export class MockRiskGradingScoreService extends RiskGradingScoreService {
  constructor(http: HttpClient, commonService: CommonService, appConfig: ApplicationConfigService) {
    super(http, commonService, appConfig);
  }

  override query(queryModel?: any): Observable<any> {
    const rows = ConfigStore.riskGradingScores()
      .filter((r) => equals(r.riskGradingCode, queryModel?.riskGradingCode))
      .filter((r) => matches(r.description, queryModel?.description));
    return respond(page(rows, queryModel));
  }

  override find(code: string): Observable<any> {
    return respond(envelope(ConfigStore.riskGradingScores().find((r) => r.code === code) ?? null));
  }

  override riskGradingList(): Observable<any> {
    return respond(page(ConfigStore.riskGradings(), { pageNumber: 0, pageSize: 10000 }));
  }

  override create(data: any): Observable<any> {
    const rows = ConfigStore.riskGradingScores();
    const created: IRiskGradingScore = {
      ...data,
      code: nextCode('RGS', rows.map((r) => r.code)),
      pendingApproval: true,
    };
    write(CONFIG_COLLECTION.riskGradingScore, [...rows, created]);
    return respond(envelope(created));
  }

  override update(data: any): Observable<any> {
    const rows = ConfigStore.riskGradingScores().map((row) =>
      row.code === data.code ? { ...row, ...data, pendingApproval: true } : row,
    );
    write(CONFIG_COLLECTION.riskGradingScore, rows);
    return respond(envelope(rows.find((r) => r.code === data.code) ?? null));
  }
}

// ---------------------------------------------------------------------------------------------
// Transaction Profile
// ---------------------------------------------------------------------------------------------

@Injectable()
export class MockTransactionProfileService extends TransactionProfileService {
  constructor(http: HttpClient, commonService: CommonService, appConfig: ApplicationConfigService) {
    super(http, commonService, appConfig);
  }

  override query(queryModel?: any): Observable<any> {
    const rows = ConfigStore.transactionProfiles()
      .filter((r) => matches(r.description, queryModel?.description))
      .filter((r) => equals(r.status, queryModel?.status));
    return respond(page(rows, queryModel));
  }

  override find(code: string): Observable<any> {
    return respond(envelope(ConfigStore.transactionProfiles().find((r) => r.transactionProfileCode === code) ?? null));
  }

  override create(data: any): Observable<any> {
    const rows = ConfigStore.transactionProfiles();
    const created = withSlab(
      {
        ...data,
        transactionProfileCode: nextCode('TP', rows.map((r) => r.transactionProfileCode)),
        status: data.status ?? true,
        pendingApproval: true,
      },
      nextCode('TPS', rows.map((r) => r.transactionProfileSlab?.transactionProfileSlabCode ?? 'TPS-000')),
    );
    write(CONFIG_COLLECTION.transactionProfile, [...rows, created]);
    return respond(envelope(created));
  }

  override update(data: any): Observable<any> {
    const rows = ConfigStore.transactionProfiles().map((row) =>
      row.transactionProfileCode === data.transactionProfileCode
        ? withSlab({ ...row, ...data, pendingApproval: true }, row.transactionProfileSlab?.transactionProfileSlabCode)
        : row,
    );
    write(CONFIG_COLLECTION.transactionProfile, rows);
    return respond(envelope(rows.find((r) => r.transactionProfileCode === data.transactionProfileCode) ?? null));
  }
}

/**
 * Normalises the read/write asymmetry the reference ships with (contracts-1 §10.3): the form
 * submits `transactionProfileSlabRequest.kycType`, everything that reads a profile expects
 * `transactionProfileSlab.ekycType`. Fold one into the other on save so downstream code — the
 * wizard's tier filter in particular — only ever deals with the read shape.
 */
function withSlab(profile: any, slabCode?: string): ITransactionProfile {
  const request = profile.transactionProfileSlabRequest;
  if (!request) {
    return profile as ITransactionProfile;
  }
  const { transactionProfileSlabRequest, ...rest } = profile;
  return {
    ...rest,
    transactionProfileSlab: {
      transactionProfileSlabCode: request.transactionProfileSlabCode || slabCode || 'TPS-000',
      description: request.description,
      ekycType: request.kycType,
      startRange: request.startRange,
      endRange: request.endRange,
    },
  } as ITransactionProfile;
}

// ---------------------------------------------------------------------------------------------
// Transaction Profile Limit
// ---------------------------------------------------------------------------------------------

@Injectable()
export class MockTransactionProfileLimitService extends TransactionProfileLimitService {
  constructor(http: HttpClient, commonService: CommonService, appConfig: ApplicationConfigService) {
    super(http, commonService, appConfig);
  }

  override query(queryModel?: any): Observable<any> {
    const rows = ConfigStore.transactionProfileLimits()
      .filter((r) => matches(r.description, queryModel?.description))
      .filter((r) => equals(r.type, queryModel?.type))
      .filter((r) => equals(r.status, queryModel?.status));
    return respond(page(rows, queryModel));
  }

  override find(code: string): Observable<any> {
    return respond(
      envelope(ConfigStore.transactionProfileLimits().find((r) => r.transactionProfileLimitCode === code) ?? null),
    );
  }

  override transactionProfileList(): Observable<any> {
    const rows = ConfigStore.transactionProfiles().filter((profile) => profile.status);
    return respond(page(rows, { pageNumber: 0, pageSize: 10000 }));
  }

  override create(data: any): Observable<any> {
    const rows = ConfigStore.transactionProfileLimits();
    const created: ITransactionProfileLimit = {
      ...data,
      transactionProfileLimitCode: nextCode('TPL', rows.map((r) => r.transactionProfileLimitCode)),
      status: data.status ?? true,
      pendingApproval: true,
    };
    write(CONFIG_COLLECTION.transactionProfileLimit, [...rows, created]);
    return respond(envelope(created));
  }

  override update(data: any): Observable<any> {
    const rows = ConfigStore.transactionProfileLimits().map((row) =>
      row.transactionProfileLimitCode === data.transactionProfileLimitCode
        ? { ...row, ...data, pendingApproval: true }
        : row,
    );
    write(CONFIG_COLLECTION.transactionProfileLimit, rows);
    return respond(
      envelope(rows.find((r) => r.transactionProfileLimitCode === data.transactionProfileLimitCode) ?? null),
    );
  }
}
